import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, getJSON } from '@/lib/api'
import type { TimelineResponse } from './ContactTimelineTab'

export function useTimelineFeed(contactId: number, query: string) {
  const [pages, setPages] = useState<TimelineResponse[]>([])
  const [metadata, setMetadata] = useState<{ contactId: number; response?: TimelineResponse }>()
  const facets = metadata?.contactId === contactId ? metadata.response : undefined
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>()
  const [failed, setFailed] = useState(false)
  const generation = useRef(0)
  const endpoint = `/api/v1/contacts/${contactId}/timeline`
  const load = useCallback(
    async (cursor?: string | null) => {
      const request = ++generation.current
      setLoading(true)
      setError(undefined)
      setFailed(false)
      if (!cursor) setPages([])
      const params = new URLSearchParams(query)
      if (cursor) params.set('cursor', cursor)
      try {
        const response = await getJSON<TimelineResponse>(`${endpoint}?${params}`)
        if (request !== generation.current) return
        setPages((current) => (cursor ? [...current, response] : [response]))
        setMetadata({ contactId, response })
      } catch (caught) {
        if (request !== generation.current) return
        if (caught instanceof ApiError && caught.status === 422) {
          setError(caught.message)
          // A shared invalid URL still needs the server's choices so staff can repair it.
          const response = await getJSON<TimelineResponse>(endpoint).catch(() => undefined)
          if (request === generation.current) setMetadata({ contactId, response })
        } else setFailed(true)
      } finally {
        if (request === generation.current) setLoading(false)
      }
    },
    [contactId, endpoint, query],
  )
  useEffect(() => {
    void load()
    return () => {
      generation.current += 1
    }
  }, [load])
  return { pages: facets ? pages : [], facets, loading, error, failed, load }
}
